import { i18n } from '@/plugins/i18n';
import store from '@/store/store';
import moment from 'moment';
import 'moment-timezone';

/** ISO 8601 calendar date: standard display and API wire format for dates in this app. */
const SHORT_DATE_FORMAT = 'YYYY-MM-DD';

/** Same calendar date with time (e.g. tender closing datetime). */
const SHORT_DATE_TIME_FORMAT = `${SHORT_DATE_FORMAT} HH:mm`;

const toMoment = (date, timeZone = 'America/New_York') => {
  if (!date) return null;
  if (typeof date === 'object' && date.epochSecond) {
    return moment(date.epochSecond * 1000);
  }
  if (typeof date === 'number') {
    return moment(date);
  }
  if (typeof date === 'string') {
    // Preserve the time and UTC offset on API timestamps before trying calendar-date formats.
    const timestamp = moment.tz(date, moment.ISO_8601, true, timeZone);
    if (timestamp.isValid()) return timestamp;
    return moment.tz(date, [SHORT_DATE_FORMAT, 'YYYY/MM/DD', 'DD/MM/YYYY', 'DD-MM-YYYY'], timeZone);
  }
  return moment.tz(date, timeZone);
};

// The API dates what it doesn't know in the year 10000, so it sorts last: there's no date to show.
const isUnknownDate = (date) => {
  const value = moment.isMoment(date) ? date : toMoment(date);
  return !value || !value.isValid() || value.year() >= 9999;
};

const formatDate = (date, format = 'DD/MM/YYYY') => {
  return date != null && !isUnknownDate(date) ? moment.tz(date, 'America/New_York').format(format) : '-';
};

/** Parses with `toMoment`, then formats as ISO calendar date (YYYY-MM-DD). */
const formatStandardDate = (value) => formatDate(toMoment(value), SHORT_DATE_FORMAT);

const formatShortDate = (date, format = 'D MMM', timeZone = 'America/New_York') => {
  if (date != null && !isUnknownDate(date)) {
    if (!moment.tz(date, timeZone).isSame(moment.tz(timeZone), 'year')) {
      format = 'DD/MM/YYYY';
    }
    return moment.tz(date, timeZone).format(format);
  } else {
    return '-';
  }
};

const getUserTimezone = () => {
  const preferences = store.getters['userPreferences/userPreferences'];
  const timezone = preferences && preferences.timezone;
  return timezone && moment.tz.zone(timezone) ? timezone : 'America/New_York';
};

const formatUserDate = (value) => {
  const timezone = getUserTimezone();
  const date = toMoment(value, timezone);
  return date && date.isValid() ? date.tz(timezone).format(SHORT_DATE_FORMAT) : '-';
};

const formatUserShortDate = (value) => {
  const timezone = getUserTimezone();
  return formatShortDate(toMoment(value, timezone), 'D MMM', timezone);
};

const formatShortDateTime = (date) => {
  if (!date) return '-';
  const m = moment.tz(date, 'America/New_York');
  return m.isValid() ? m.format(SHORT_DATE_TIME_FORMAT) : '-';
};

/**
 * Long, locale-aware calendar date for an API timestamp (e.g. "30 juillet 2026").
 *
 * Preserves the instant and formats its calendar date in Eastern time.
 */
const formatLongDate = (value) => {
  if (!value) return '-';
  const m = moment.tz(value, 'America/New_York');
  return m.isValid() ? m.format('D MMMM YYYY') : '-';
};

const formatDateToString = (date) => {
  if (!date) return null;

  if (typeof date === 'string') {
    return formatDate(toMoment(date), SHORT_DATE_FORMAT);
  }
  return formatDate(date, SHORT_DATE_FORMAT);
};

const getDaysBeforeClosing = (closingDate) => {
  if (!closingDate) {
    return null;
  }

  const closingFormatted = toMoment(closingDate);
  let now = moment().startOf('minutes');
  let timeDiff = closingFormatted.diff(now, 'minutes') + 1;
  if (timeDiff < 1 || isNaN(timeDiff)) {
    return null;
  }
  let Dhm = minutesToDhm(timeDiff);
  return Dhm;
};

const minutesToDhm = (minutes) => {
  let d = Math.floor(minutes / (60 * 24));
  let h = Math.floor((minutes % (60 * 24)) / 60);
  let m = Math.floor(minutes % 60);

  let dDisplay = d > 0 ? d : '';
  let hDisplay = h > 0 ? h : '';
  let mDisplay = m > 0 ? m : '';
  return [dDisplay, hDisplay, mDisplay];
};

const getDhm = (timeLeft) => {
  let build = '';

  build += timeLeft[0] ? timeLeft[0] + i18n.t('page.tenders.day') + ' ' : '';
  build += timeLeft[1] ? timeLeft[1] + i18n.t('page.tenders.hour') + ' ' : '';
  build += timeLeft[2] ? timeLeft[2] + i18n.t('page.tenders.minute') : '';
  return build;
};

const getHm = (timeLeft) => {
  let build = '';

  build += timeLeft[1] ? timeLeft[1] + i18n.t('page.tenders.hour') + ' ' : '';
  build += timeLeft[2] ? timeLeft[2] + i18n.t('page.tenders.minute') : '';
  return build;
};

const getNow = () => {
  return moment().format('YYYY-MM-DD-HH-mm');
};

const getToday = () => {
  return moment().format(SHORT_DATE_FORMAT);
};

const getStartOfYear = () => {
  var startOfYear = moment().startOf('year');
  return startOfYear.format(SHORT_DATE_FORMAT);
};
const getEndOfYear = () => {
  var endOfYear = moment().endOf('year');
  return endOfYear.format(SHORT_DATE_FORMAT);
};

const datesArrayToObject = (dates) => {
  let publishedAfter;
  let publishedBefore;

  const userPreferences = store.getters['userPreferences/userPreferences'];

  if (!userPreferences) return;
  const userTimezone = userPreferences.timezone;

  if (dates && dates.length === 1) {
    publishedAfter = moment.tz(dates[0], userTimezone).startOf('day');
    publishedBefore = moment.tz(dates[0], userTimezone).endOf('day');
  } else if (dates && dates.length === 2) {
    publishedAfter = moment.tz(dates[0], userTimezone).startOf('day');
    publishedBefore = moment.tz(dates[1], userTimezone).endOf('day');
  }

  return { publishedAfter, publishedBefore };
};

const isBefore = (startDate, endDate) => {
  return moment(startDate).isBefore(endDate);
};

const getPreviousPeriod = (startDate, endDate) => {
  if (isPeriod(startDate, endDate, 'week')) {
    return calculatePreviousPeriod(startDate, 'week');
  } else if (isPeriod(startDate, endDate, 'month')) {
    return calculatePreviousPeriod(startDate, 'month');
  } else if (isPeriod(startDate, endDate, 'year')) {
    return calculatePreviousPeriod(startDate, 'year');
  } else {
    const delta = endDate.diff(startDate, 'days') + 1;
    return [
      formatDateToString(startDate.clone().subtract(delta, 'days')),
      formatDateToString(startDate.clone().subtract(1, 'days')),
    ];
  }
};

const getNextPeriod = (startDate, endDate) => {
  if (isPeriod(startDate, endDate, 'week')) {
    return calculateNextPeriod(startDate, 'week');
  } else if (isPeriod(startDate, endDate, 'month')) {
    return calculateNextPeriod(startDate, 'month');
  } else if (isPeriod(startDate, endDate, 'year')) {
    return calculateNextPeriod(startDate, 'year');
  } else {
    const delta = endDate.diff(startDate, 'days') + 1;
    return [formatDateToString(endDate.clone().add(1, 'days')), formatDateToString(endDate.clone().add(delta, 'days'))];
  }
};

const getCurrentPeriod = (unit) => {
  return [getStartOfPeriod(moment(), unit), getEndOfPeriod(moment(), unit)];
};

// moment.js unit
// https://momentjs.com/docs/#/durations/creating/
// returns the start and end date of the previous period
const calculatePreviousPeriod = (date, unit) => {
  return [getStartOfPeriod(date.clone().subtract(1, unit), unit), getEndOfPeriod(date.clone().subtract(1, unit), unit)];
};

// returns the start and end date of the next period
const calculateNextPeriod = (date, unit) => {
  return [getStartOfPeriod(date.clone().add(1, unit), unit), getEndOfPeriod(date.clone().add(1, unit), unit)];
};

const isPeriod = (startDate, endDate, unit) => {
  // start of week is Monday
  if (unit === 'week') {
    return (
      startDate.isSame(startDate.clone().startOf('isoWeek'), 'day') &&
      endDate.isSame(startDate.clone().endOf('isoWeek'), 'day')
    );
  }

  return (
    startDate.isSame(startDate.clone().startOf(unit), 'day') && endDate.isSame(startDate.clone().endOf(unit), 'day')
  );
};

const getStartOfPeriod = (date, unit) => {
  if (unit === 'week') {
    return formatDateToString(date.clone().startOf('isoWeek'));
  }
  return formatDateToString(date.clone().startOf(unit));
};

const getEndOfPeriod = (date, unit) => {
  if (unit === 'week') {
    return formatDateToString(date.clone().endOf('isoWeek'));
  }
  return formatDateToString(date.clone().endOf(unit));
};

export {
  isUnknownDate,
  formatUserDate,
  formatUserShortDate,
  datesArrayToObject,
  formatDate,
  formatStandardDate,
  formatShortDate,
  formatShortDateTime,
  formatLongDate,
  formatDateToString,
  SHORT_DATE_FORMAT,
  SHORT_DATE_TIME_FORMAT,
  getDaysBeforeClosing,
  getDhm,
  getHm,
  getNow,
  getToday,
  getStartOfYear,
  getEndOfYear,
  isBefore,
  toMoment,
  getPreviousPeriod,
  getNextPeriod,
  getCurrentPeriod,
  getStartOfPeriod,
  getEndOfPeriod,
  calculatePreviousPeriod,
};
